import { supabase } from './supabaseClient';

// ============================================
// QUANT PROBLEMS
// ============================================

export const quantProblemsService = {
  // Get all quant problems for a user
  getAll: async (userId: string) => {
    const { data, error } = await supabase
      .from('quant_problems')
      .select('*')
      .eq('user_id', userId)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Get quant problems by date range
  getByDateRange: async (userId: string, startDate: string, endDate: string) => {
    const { data, error } = await supabase
      .from('quant_problems')
      .select('*')
      .eq('user_id', userId)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Get a single quant problem by ID
  getById: async (id: string) => {
    const { data, error } = await supabase
      .from('quant_problems')
      .select('*')
      .eq('id', id)
      .single();
    return { data, error };
  },

  // Insert a new quant problem
  insert: async (problem: {
    user_id: string;
    problem_number: number;
    time_spent: number;
    passed: boolean;
    timestamp: number;
    date: string;
    error_type?: string;
    error_made?: string;
    difficulty?: string;
    notes?: string;
    image?: string;
  }) => {
    const { data, error } = await supabase
      .from('quant_problems')
      .insert(problem)
      .select()
      .single();
    return { data, error };
  },

  // Insert multiple quant problems (batch)
  insertBatch: async (problems: Array<{
    user_id: string;
    problem_number: number;
    time_spent: number;
    passed: boolean;
    timestamp: number;
    date: string;
    error_type?: string;
    error_made?: string;
    difficulty?: string;
    notes?: string;
    image?: string;
  }>) => {
    const { data, error } = await supabase
      .from('quant_problems')
      .insert(problems)
      .select();
    return { data, error };
  },

  // Update a quant problem
  update: async (id: string, updates: Partial<{
    problem_number: number;
    time_spent: number;
    passed: boolean;
    error_type: string;
    error_made: string;
    difficulty: string;
    notes: string;
    image: string;
    ai_solution: string;
  }>) => {
    const { data, error } = await supabase
      .from('quant_problems')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  // Delete a quant problem
  delete: async (id: string) => {
    const { error } = await supabase
      .from('quant_problems')
      .delete()
      .eq('id', id);
    return { error };
  },

  // Delete all quant problems for a user
  deleteAllForUser: async (userId: string) => {
    const { error } = await supabase
      .from('quant_problems')
      .delete()
      .eq('user_id', userId);
    return { error };
  }
};

// ============================================
// QUANT ERRORS
// ============================================

export const quantErrorsService = {
  // Get all quant errors for a user
  getAll: async (userId: string) => {
    const { data, error } = await supabase
      .from('quant_errors')
      .select('*')
      .eq('user_id', userId)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Get quant errors by problem number
  getByProblemNumber: async (userId: string, problemNumber: number) => {
    const { data, error } = await supabase
      .from('quant_errors')
      .select('*')
      .eq('user_id', userId)
      .eq('problem_number', problemNumber)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Get a single quant error by ID
  getById: async (id: string) => {
    const { data, error } = await supabase
      .from('quant_errors')
      .select('*')
      .eq('id', id)
      .single();
    return { data, error };
  },

  // Insert a new quant error
  insert: async (error: {
    user_id: string;
    problem_number: number;
    error_type: string;
    error_made: string;
    difficulty: string;
    notes?: string;
    image?: string;
    time_spent?: number;
    timestamp: number;
    date: string;
  }) => {
    const { data, error: err } = await supabase
      .from('quant_errors')
      .insert(error)
      .select()
      .single();
    return { data, error: err };
  },

  // Insert multiple quant errors (batch)
  insertBatch: async (errors: Array<{
    user_id: string;
    problem_number: number;
    error_type: string;
    error_made: string;
    difficulty: string;
    notes?: string;
    image?: string;
    time_spent?: number;
    timestamp: number;
    date: string;
  }>) => {
    const { data, error } = await supabase
      .from('quant_errors')
      .insert(errors)
      .select();
    return { data, error };
  },

  // Update a quant error
  update: async (id: string, updates: Partial<{
    error_type: string;
    error_made: string;
    difficulty: string;
    notes: string;
    image: string;
    time_spent: number;
  }>) => {
    const { data, error } = await supabase
      .from('quant_errors')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  // Delete a quant error
  delete: async (id: string) => {
    const { error } = await supabase
      .from('quant_errors')
      .delete()
      .eq('id', id);
    return { error };
  },

  // Delete all quant errors for a user
  deleteAllForUser: async (userId: string) => {
    const { error } = await supabase
      .from('quant_errors')
      .delete()
      .eq('user_id', userId);
    return { error };
  }
};

// ============================================
// QUANT FEEDBACK
// ============================================

export const quantFeedbackService = {
  // Get all quant feedback for a user
  getAll: async (userId: string) => {
    const { data, error } = await supabase
      .from('quant_feedback')
      .select('*')
      .eq('user_id', userId)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Get feedback by problem number
  getByProblemNumber: async (userId: string, problemNumber: number) => {
    const { data, error } = await supabase
      .from('quant_feedback')
      .select('*')
      .eq('user_id', userId)
      .eq('problem_number', problemNumber)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Insert a new quant feedback
  insert: async (feedback: {
    user_id: string;
    problem_number: number;
    error_type?: string;
    error_made?: string;
    difficulty?: string;
    notes?: string;
    image?: string;
    timestamp: number;
    date: string;
  }) => {
    const { data, error } = await supabase
      .from('quant_feedback')
      .insert(feedback)
      .select()
      .single();
    return { data, error };
  },

  // Update quant feedback
  update: async (id: string, updates: Partial<{
    problem_number: number;
    error_type: string;
    error_made: string;
    difficulty: string;
    notes: string;
    image: string;
  }>) => {
    const { data, error } = await supabase
      .from('quant_feedback')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  // Delete quant feedback
  delete: async (id: string) => {
    const { error } = await supabase
      .from('quant_feedback')
      .delete()
      .eq('id', id);
    return { error };
  },

  // Delete all quant feedback for a user
  deleteAll: async (userId: string) => {
    const { error } = await supabase
      .from('quant_feedback')
      .delete()
      .eq('user_id', userId);
    return { error };
  }
};

// ============================================
// STUDY TIME
// ============================================

export const studyTimeService = {
  // Upsert study time (insert or update if exists)
  upsert: async (userId: string, mode: string, date: string, timeSeconds: number) => {
    const { data, error } = await supabase
      .from('study_time')
      .upsert({
        user_id: userId,
        mode,
        date,
        time_seconds: timeSeconds,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id,mode,date'
      })
      .select()
      .single();
    return { data, error };
  },

  // Get study time for a user
  getByUser: async (userId: string, mode?: string, date?: string) => {
    let query = supabase
      .from('study_time')
      .select('*')
      .eq('user_id', userId);

    if (mode) {
      query = query.eq('mode', mode);
    }
    if (date) {
      query = query.eq('date', date);
    }

    const { data, error } = await query.order('date', { ascending: false });
    return { data, error };
  },

  // Get total study time for a user (all modes)
  getTotal: async (userId: string) => {
    const { data, error } = await supabase
      .from('study_time')
      .select('time_seconds')
      .eq('user_id', userId);

    if (error) return { total: 0, error };

    const total = data?.reduce((sum, record) => sum + (record.time_seconds || 0), 0) || 0;
    return { total, error: null };
  },

  // Get study time for a specific date range
  getByDateRange: async (userId: string, startDate: string, endDate: string, mode?: string) => {
    let query = supabase
      .from('study_time')
      .select('*')
      .eq('user_id', userId)
      .gte('date', startDate)
      .lte('date', endDate);

    if (mode) {
      query = query.eq('mode', mode);
    }

    const { data, error } = await query.order('date', { ascending: false });
    return { data, error };
  }
};

// ============================================
// FLASHCARD ANSWERS
// ============================================

export const flashcardAnswersService = {
  // Get all flashcard answers for a user
  getAll: async (userId: string) => {
    const { data, error } = await supabase
      .from('flashcard_answers')
      .select('*')
      .eq('user_id', userId)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Get correct answers only
  getCorrect: async (userId: string) => {
    const { data, error } = await supabase
      .from('flashcard_answers')
      .select('*')
      .eq('user_id', userId)
      .eq('correct', true)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Get incorrect answers only
  getIncorrect: async (userId: string) => {
    const { data, error } = await supabase
      .from('flashcard_answers')
      .select('*')
      .eq('user_id', userId)
      .eq('correct', false)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Insert a flashcard answer
  insert: async (answer: {
    user_id: string;
    word_id: string;
    word: string;
    definition?: string;
    correct: boolean;
    timestamp: number;
    date: string;
  }) => {
    const { data, error } = await supabase
      .from('flashcard_answers')
      .upsert(answer, {
        onConflict: 'user_id,word_id'
      })
      .select()
      .single();
    return { data, error };
  },

  // Insert multiple flashcard answers (batch)
  insertBatch: async (answers: Array<{
    user_id: string;
    word_id: string;
    word: string;
    definition?: string;
    correct: boolean;
    timestamp: number;
    date: string;
  }>) => {
    const { data, error } = await supabase
      .from('flashcard_answers')
      .upsert(answers, {
        onConflict: 'user_id,word_id'
      })
      .select();
    return { data, error };
  },

  // Delete all answers for a user
  deleteAll: async (userId: string) => {
    const { error } = await supabase
      .from('flashcard_answers')
      .delete()
      .eq('user_id', userId);
    return { error };
  },

  // Delete a flashcard answer
  delete: async (id: string) => {
    const { error } = await supabase
      .from('flashcard_answers')
      .delete()
      .eq('id', id);
    return { error };
  }
};

// ============================================
// SYNONYM PROGRESS
// ============================================

export const synonymProgressService = {
  // Get all synonym progress for a user
  getAll: async (userId: string) => {
    const { data, error } = await supabase
      .from('synonym_progress')
      .select('*')
      .eq('user_id', userId)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Get completed words
  getCompleted: async (userId: string) => {
    const { data, error } = await supabase
      .from('synonym_progress')
      .select('*')
      .eq('user_id', userId)
      .eq('completed', true)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Get incomplete words
  getIncomplete: async (userId: string) => {
    const { data, error } = await supabase
      .from('synonym_progress')
      .select('*')
      .eq('user_id', userId)
      .eq('completed', false)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Upsert synonym progress (insert or update if exists)
  upsert: async (progress: {
    user_id: string;
    word_id: string;
    word: string;
    completed: boolean;
    timestamp: number;
    date: string;
  }) => {
    const { data, error } = await supabase
      .from('synonym_progress')
      .upsert(progress, {
        onConflict: 'user_id,word_id'
      })
      .select()
      .single();
    return { data, error };
  },

  // Update completion status
  updateCompletion: async (userId: string, wordId: string, completed: boolean) => {
    const { data, error } = await supabase
      .from('synonym_progress')
      .update({ 
        completed,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .eq('word_id', wordId)
      .select()
      .single();
    return { data, error };
  },

  // Delete synonym progress
  delete: async (userId: string, wordId: string) => {
    const { error } = await supabase
      .from('synonym_progress')
      .delete()
      .eq('user_id', userId)
      .eq('word_id', wordId);
    return { error };
  },

  // Delete all synonym progress for a user
  deleteAll: async (userId: string) => {
    const { error } = await supabase
      .from('synonym_progress')
      .delete()
      .eq('user_id', userId);
    return { error };
  }
};

// ============================================
// HARD TERMS ANSWERS
// ============================================

export const hardTermsAnswersService = {
  // Get all hard terms answers for a user
  getAll: async (userId: string) => {
    const { data, error } = await supabase
      .from('hard_terms_answers')
      .select('*')
      .eq('user_id', userId)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Get correct answers only
  getCorrect: async (userId: string) => {
    const { data, error } = await supabase
      .from('hard_terms_answers')
      .select('*')
      .eq('user_id', userId)
      .eq('correct', true)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Get incorrect answers only
  getIncorrect: async (userId: string) => {
    const { data, error } = await supabase
      .from('hard_terms_answers')
      .select('*')
      .eq('user_id', userId)
      .eq('correct', false)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Insert a hard terms answer
  insert: async (answer: {
    user_id: string;
    word_id: string;
    word: string;
    definition?: string;
    correct: boolean;
    timestamp: number;
    date: string;
  }) => {
    const { data, error } = await supabase
      .from('hard_terms_answers')
      .upsert(answer, {
        onConflict: 'user_id,word_id'
      })
      .select()
      .single();
    return { data, error };
  },

  // Insert multiple hard terms answers (batch)
  insertBatch: async (answers: Array<{
    user_id: string;
    word_id: string;
    word: string;
    definition?: string;
    correct: boolean;
    timestamp: number;
    date: string;
  }>) => {
    const { data, error } = await supabase
      .from('hard_terms_answers')
      .upsert(answers, {
        onConflict: 'user_id,word_id'
      })
      .select();
    return { data, error };
  },

  // Delete a hard terms answer
  delete: async (id: string) => {
    const { error } = await supabase
      .from('hard_terms_answers')
      .delete()
      .eq('id', id);
    return { error };
  },

  deleteAll: async (userId: string) => {
    const { error } = await supabase
      .from('hard_terms_answers')
      .delete()
      .eq('user_id', userId);
    return { error };
  }
};

// ============================================
// FAST ATTACK PROGRESS
// ============================================

export const fastAttackProgressService = {
  // Get fast attack progress for a user
  get: async (userId: string) => {
    const { data, error } = await supabase
      .from('fast_attack_progress')
      .select('*')
      .eq('user_id', userId)
      .single();
    return { data, error };
  },

  // Upsert fast attack progress (insert or update if exists)
  upsert: async (progress: {
    user_id: string;
    total_days: number;
    current_day: number;
    progress: any; // JSONB object
  }) => {
    const { data, error } = await supabase
      .from('fast_attack_progress')
      .upsert({
        ...progress,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      })
      .select()
      .single();
    return { data, error };
  },

  // Update progress object
  updateProgress: async (userId: string, progress: any) => {
    const { data, error } = await supabase
      .from('fast_attack_progress')
      .update({
        progress,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .select()
      .single();
    return { data, error };
  },

  // Update current day
  updateCurrentDay: async (userId: string, currentDay: number) => {
    const { data, error } = await supabase
      .from('fast_attack_progress')
      .update({
        current_day: currentDay,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .select()
      .single();
    return { data, error };
  },

  // Delete fast attack progress
  delete: async (userId: string) => {
    const { error } = await supabase
      .from('fast_attack_progress')
      .delete()
      .eq('user_id', userId);
    return { error };
  }
};

// ============================================
// REPORTED ISSUES
// ============================================

export const reportedIssuesService = {
  // Get all reported issues for a user
  getAll: async (userId: string) => {
    const { data, error } = await supabase
      .from('reported_issues')
      .select('*')
      .eq('user_id', userId)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Get reported issues by game mode
  getByGameMode: async (userId: string, gameMode: string) => {
    const { data, error } = await supabase
      .from('reported_issues')
      .select('*')
      .eq('user_id', userId)
      .eq('game_mode', gameMode)
      .order('timestamp', { ascending: false });
    return { data, error };
  },

  // Insert a reported issue
  insert: async (issue: {
    user_id: string;
    game_mode: string;
    word_id?: string;
    word?: string;
    note?: string;
    timestamp: number;
    date: string;
  }) => {
    const { data, error } = await supabase
      .from('reported_issues')
      .insert(issue)
      .select()
      .single();
    return { data, error };
  },

  // Delete a reported issue
  delete: async (id: string) => {
    const { error } = await supabase
      .from('reported_issues')
      .delete()
      .eq('id', id);
    return { error };
  },

  // Delete all reported issues for a user/game mode
  deleteByGameMode: async (userId: string, gameMode: string) => {
    const { error } = await supabase
      .from('reported_issues')
      .delete()
      .eq('user_id', userId)
      .eq('game_mode', gameMode);
    return { error };
  }
};

// ============================================
// USER PROGRESS STATE (for resuming games)
// ============================================

export const userProgressStateService = {
  // Get all progress state for a user
  getAll: async (userId: string) => {
    const { data, error } = await supabase
      .from('user_progress_state')
      .select('*')
      .eq('user_id', userId);
    return { data, error };
  },

  // Get progress state for a specific game mode
  getByGameMode: async (userId: string, gameMode: string) => {
    const { data, error } = await supabase
      .from('user_progress_state')
      .select('*')
      .eq('user_id', userId)
      .eq('game_mode', gameMode);
    return { data, error };
  },

  // Get a specific state key for a game mode
  get: async (userId: string, gameMode: string, stateKey: string) => {
    const { data, error } = await supabase
      .from('user_progress_state')
      .select('*')
      .eq('user_id', userId)
      .eq('game_mode', gameMode)
      .eq('state_key', stateKey)
      .single();
    return { data, error };
  },

  // Upsert a progress state (insert or update if exists)
  upsert: async (state: {
    user_id: string;
    game_mode: string;
    state_key: string;
    state_value: string;
  }) => {
    const { data, error } = await supabase
      .from('user_progress_state')
      .upsert({
        ...state,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id,game_mode,state_key'
      })
      .select()
      .single();
    return { data, error };
  },

  // Update a progress state
  update: async (userId: string, gameMode: string, stateKey: string, stateValue: string) => {
    const { data, error } = await supabase
      .from('user_progress_state')
      .update({
        state_value: stateValue,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .eq('game_mode', gameMode)
      .eq('state_key', stateKey)
      .select()
      .single();
    return { data, error };
  },

  // Delete a progress state
  delete: async (userId: string, gameMode: string, stateKey: string) => {
    const { error } = await supabase
      .from('user_progress_state')
      .delete()
      .eq('user_id', userId)
      .eq('game_mode', gameMode)
      .eq('state_key', stateKey);
    return { error };
  }
};

// ============================================
// CUSTOM WORDS
// ============================================

export const customWordsService = {
  list: async (userId: string) => {
    return supabase
      .from('custom_words')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
  },
  insert: async (payload: {
    user_id: string;
    word: string;
    definition: string;
    similar_words_category?: string;
    similar_words?: string;
    source?: string;
  }) => {
    const { data, error } = await supabase
      .from('custom_words')
      .insert(payload)
      .select()
      .single();
    return { data, error };
  }
};

// ============================================
// CONVENIENCE EXPORT - All services in one object
// ============================================

export const dataService = {
  quantProblems: quantProblemsService,
  quantErrors: quantErrorsService,
  quantFeedback: quantFeedbackService,
  studyTime: studyTimeService,
  flashcardAnswers: flashcardAnswersService,
  synonymProgress: synonymProgressService,
  hardTermsAnswers: hardTermsAnswersService,
  fastAttackProgress: fastAttackProgressService,
  reportedIssues: reportedIssuesService,
  userProgressState: userProgressStateService,
  customWords: customWordsService
};

